import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PasswordGate.css';

const STORAGE_KEY = 'website_access_expires';
const ACCESS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function hasValidAccess() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    return Date.now() < parseInt(raw, 10);
  } catch {
    return false;
  }
}

function grantAccess() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now() + ACCESS_TTL_MS));
  } catch {
    // Storage unavailable — silently skip; user re-enters on next visit
  }
}

/**
 * PasswordGate — WCAG 2.1 AA compliant access gate for the beta website.
 *
 * Gate is active when admin has set a password via Admin Backend → Einstellungen.
 * When no password is configured the site opens directly.
 * Granted access is cached in localStorage for 7 days.
 */
function PasswordGate({ children }) {
  // 'checking' → initial status probe | 'gate' → show form | 'open' → show site
  const [status, setStatus] = useState('checking');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Fast path: we already have cached access
    if (hasValidAccess()) {
      setStatus('open');
      return;
    }

    // Ask the backend whether the gate is active
    fetch('/api/website-auth/status')
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(data => {
        if (data.gate_active) {
          setStatus('gate');
        } else {
          grantAccess();
          setStatus('open');
        }
      })
      .catch(() => {
        // Cannot reach backend → fail closed (show gate, block entry)
        setStatus('gate');
      });
  }, []);

  // Focus password input as soon as the gate form becomes visible
  useEffect(() => {
    if (status === 'gate') {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [status]);

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();
      if (isSubmitting || !password.trim()) return;

      setIsSubmitting(true);
      setError('');

      try {
        const res = await fetch('/api/website-auth', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password }),
        });

        if (res.ok) {
          grantAccess();
          setStatus('open');
          return;
        }

        const msg =
          res.status === 401
            ? 'Falsches Passwort. Bitte versuche es erneut.'
            : 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.';
        setError(msg);
        inputRef.current?.focus();
      } catch {
        setError('Verbindungsfehler. Bitte überprüfe deine Internetverbindung.');
        inputRef.current?.focus();
      } finally {
        setIsSubmitting(false);
      }
    },
    [password, isSubmitting],
  );

  if (status === 'checking') {
    return (
      <div className="pg-overlay pg-overlay--checking" role="status" aria-label="Seite wird geladen">
        <div className="pg-spinner" aria-hidden="true" />
      </div>
    );
  }

  if (status === 'open') {
    return children;
  }

  return (
    <div className="pg-overlay" lang="de">
      {/* Skip-link: keyboard users who know the password skip straight to it */}
      <a className="pg-skip-link" href="#pg-password">
        Direkt zum Passwortfeld springen
      </a>

      <main className="pg-card" aria-labelledby="pg-heading">
        <span className="pg-logo" aria-hidden="true">U</span>

        <h1 id="pg-heading" className="pg-heading">
          UNIVERSE
        </h1>
        <p className="pg-subheading">
          Diese Website ist zurzeit nur für eingeladene Gäste zugänglich.
        </p>

        <form
          className="pg-form"
          onSubmit={handleSubmit}
          noValidate
          aria-label="Passwort eingeben, um Zugang zu erhalten"
        >
          <div className="pg-field">
            <label htmlFor="pg-password" className="pg-label">
              Zugangspasswort
            </label>
            <input
              id="pg-password"
              ref={inputRef}
              type="password"
              className={`pg-input${error ? ' pg-input--error' : ''}`}
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                // Clear stale error as user types so they get fresh feedback
                if (error) setError('');
              }}
              autoComplete="current-password"
              aria-required="true"
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? 'pg-error' : undefined}
              disabled={isSubmitting}
              placeholder="Passwort eingeben"
            />
          </div>

          {/* role="alert" announces the error immediately to screen readers */}
          {error && (
            <p id="pg-error" className="pg-error" role="alert" aria-live="assertive">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="pg-button"
            disabled={isSubmitting || !password.trim()}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? 'Wird überprüft…' : 'Zugang beantragen'}
          </button>
        </form>
      </main>
    </div>
  );
}

export default PasswordGate;
